import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "../i18n/I18nProvider";
import { HERO } from "../constants/testIds";
import MagneticButton from "../components/MagneticButton";
import ParticleField from "../components/ParticleField";
import { WordsReveal } from "../components/Reveal";
import { openWhatsapp } from "../lib/whatsapp";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t } = useI18n();

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const textY = useTransform(scrollY, [0, 800], [0, -60]);

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative min-h-screen w-full overflow-hidden bg-black text-white isolate"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 -z-20"
        aria-hidden="true"
      >
        <img
          src="https://images.unsplash.com/photo-1504802318913-d3f9a487448c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt=""
          className="w-full h-[120%] object-cover opacity-[0.35]"
        />
      </motion.div>

      {/* Gradient overlay for depth */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-black/40 via-black/70 to-black"
      />

      {/* Blueprint grid */}
      <div aria-hidden className="absolute inset-0 -z-10 gaelon-grid-bg opacity-60" />

      {/* Particles */}
      <ParticleField count={70} speed={0.1} className="-z-10" />

      {/* Top hairline */}
      <div className="absolute top-[72px] left-0 right-0 h-px bg-white/5" />

      {/* Side technical labels */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 origin-left -rotate-90 gap-3 items-center text-[10px] tracking-[0.4em] uppercase text-white/40 font-mono">
        <span>GAELON / SYS.01</span>
        <span className="w-10 h-px bg-white/20" />
        <span>2026</span>
      </div>
      <div className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 origin-right rotate-90 gap-3 items-center text-[10px] tracking-[0.4em] uppercase text-white/40 font-mono">
        <span>INTELLIGENCE INFRASTRUCTURE</span>
        <span className="w-10 h-px bg-white/20" />
        <span>v1</span>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-40 md:pt-52 lg:pt-56 pb-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
          className="mb-10 flex items-center gap-4 text-[10px] tracking-[0.4em] uppercase font-mono text-white/60"
        >
          <span className="w-2 h-2 bg-white gaelon-pulse rounded-full" />
          {t.hero.eyebrow}
        </motion.div>

        <h1
          data-testid={HERO.headline}
          className="font-display tracking-display text-white text-[44px] sm:text-[68px] md:text-[92px] lg:text-[120px] leading-[0.92] font-medium"
        >
          <span className="block"><WordsReveal text={t.hero.headlineLine1} /></span>
          <span className="block"><WordsReveal text={t.hero.headlineLine2} delay={0.05} /></span>
          <span className="block text-white/55"><WordsReveal text={t.hero.headlineLine3} delay={0.1} /></span>
        </h1>

        <motion.p
          data-testid={HERO.subheadline}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.6 }}
          className="mt-12 max-w-[640px] text-white/70 text-[15px] md:text-[17px] leading-[1.6]"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.8 }}
          className="mt-14 flex flex-wrap items-center gap-4"
        >
          <MagneticButton
            variant="primary"
            data-testid={HERO.ctaBookCall}
            onClick={() => scrollToId("contact")}
          >
            {t.hero.ctaPrimary}
          </MagneticButton>
          <MagneticButton
            variant="secondary"
            data-testid={HERO.ctaExplore}
            onClick={() => scrollToId("capabilities")}
          >
            {t.hero.ctaSecondary}
          </MagneticButton>
        </motion.div>
      </motion.div>

      {/* Bottom scroll hint */}
      <motion.div
        data-testid={HERO.scrollHint}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/45 font-mono">
          {t.hero.scrollHint}
        </span>
        <span className="block w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
